import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getNotificationPreferences,
  updateNotificationPreference,
} from '../../../api/notificationPreferences';
import {
  getMyWebhooks,
  addWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  TEAMS_SUPPORTED_EVENTS,
} from '../../../api/webhookChannels';
import { SharedWebhookGuide } from '../../common/SharedUI';
import './NotificationPreferences.scss';

const EVENT_LABELS = {
  ticket_assigned: '被指派工作項目',
  mentioned: '留言中被 @mention',
  work_item_comment: '追蹤的工作項目有新留言',
  deliverable_handoff: '收到交付項目交棒',
};

/** 遮罩 webhook URL，只顯示前後各 12 字元 */
const maskUrl = (url) => {
  if (!url || url.length <= 30) return url;
  return `${url.slice(0, 12)}...${url.slice(-12)}`;
};

/** 取得 subscribed_events 的顯示文字 */
const getSubscriptionLabel = (events) => {
  if (!events || events.length === 0) return '全部事件';
  return events.map((e) => EVENT_LABELS[e] || e).join('、');
};

const NotificationPreferences = ({ isOpen, onClose }) => {
  const { profile } = useAuth();
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);

  // Webhook state
  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ label: '我的事件通知', webhook_url: '', subscribed_events: [] });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const loadAll = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    setWebhooksLoading(true);

    const [prefsResult, webhooksResult] = await Promise.all([
      getNotificationPreferences(profile.id),
      getMyWebhooks(profile.id),
    ]);

    setPreferences(prefsResult.data || []);
    setWebhooks(webhooksResult.data || []);
    setLoading(false);
    setWebhooksLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    if (isOpen && profile?.id) {
      // 重置表單狀態
      setShowAddForm(false);
      setEditingId(null);
      setFormData({ label: '我的事件通知', webhook_url: '', subscribed_events: [] });
      setTestResult(null);
      setDeleteConfirmId(null);
      loadAll();
    }
  }, [isOpen, profile?.id, loadAll]);

  // === 通知偏好 toggle ===
  const handleToggle = async (eventType, field) => {
    const pref = preferences.find((p) => p.event_type === eventType);
    if (!pref) return;

    const newValue = !pref[field];
    setPreferences((prev) =>
      prev.map((p) =>
        p.event_type === eventType ? { ...p, [field]: newValue } : p
      )
    );

    const { error } = await updateNotificationPreference(profile.id, eventType, {
      [field]: newValue,
    });

    if (error) {
      setPreferences((prev) =>
        prev.map((p) =>
          p.event_type === eventType ? { ...p, [field]: !newValue } : p
        )
      );
    }
  };

  // === Webhook 管理 ===
  const resetForm = () => {
    setFormData({ label: '我的事件通知', webhook_url: '', subscribed_events: [] });
    setShowAddForm(false);
    setEditingId(null);
    setTestResult(null);
  };

  const handleAddClick = () => {
    // 預設全選所有支援的事件
    setFormData({
      label: '我的事件通知',
      webhook_url: '',
      subscribed_events: TEAMS_SUPPORTED_EVENTS.map((e) => e.value),
    });
    setShowAddForm(true);
    setEditingId(null);
    setTestResult(null);
  };

  const handleEditClick = (webhook) => {
    setFormData({
      label: webhook.label || '我的事件通知',
      webhook_url: webhook.webhook_url,
      subscribed_events: webhook.subscribed_events || TEAMS_SUPPORTED_EVENTS.map((e) => e.value),
    });
    setEditingId(webhook.id);
    setShowAddForm(false);
    setTestResult(null);
  };

  const handleEventToggle = (eventValue) => {
    setFormData((prev) => {
      const events = prev.subscribed_events.includes(eventValue)
        ? prev.subscribed_events.filter((e) => e !== eventValue)
        : [...prev.subscribed_events, eventValue];
      return { ...prev, subscribed_events: events };
    });
  };

  const handleTest = async () => {
    if (!formData.webhook_url) return;
    setTesting(true);
    setTestResult(null);
    const result = await testWebhook(formData.webhook_url, profile?.email || null);
    setTestResult(result);
    setTesting(false);
  };

  const handleSave = async () => {
    if (!formData.webhook_url || !profile?.id) return;

    setSaving(true);
    const payload = {
      webhook_url: formData.webhook_url,
      label: formData.label || '我的事件通知',
      // 全選 = null（代表訂閱全部）
      subscribed_events:
        formData.subscribed_events.length === TEAMS_SUPPORTED_EVENTS.length
          ? null
          : formData.subscribed_events,
    };

    let result;
    if (editingId) {
      result = await updateWebhook(editingId, payload);
    } else {
      result = await addWebhook({ user_id: profile.id, ...payload });
    }

    setSaving(false);

    if (!result.error) {
      resetForm();
      // Reload webhooks
      const { data } = await getMyWebhooks(profile.id);
      setWebhooks(data || []);
    }
  };

  const handleDelete = async (id) => {
    const { error } = await deleteWebhook(id);
    if (!error) {
      setWebhooks((prev) => prev.filter((w) => w.id !== id));
      setDeleteConfirmId(null);
    }
  };

  const handleActiveToggle = async (webhook) => {
    const newValue = !webhook.is_active;
    // Optimistic update
    setWebhooks((prev) =>
      prev.map((w) => (w.id === webhook.id ? { ...w, is_active: newValue } : w))
    );

    const { error } = await updateWebhook(webhook.id, { is_active: newValue });
    if (error) {
      setWebhooks((prev) =>
        prev.map((w) => (w.id === webhook.id ? { ...w, is_active: !newValue } : w))
      );
    }
  };

  if (!isOpen) return null;

  const isFormVisible = showAddForm || editingId !== null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="notification-preferences-modal">
        <div className="modal-header">
          <h2>通知設定</h2>
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="prefs-loading">載入中...</div>
          ) : (
            <>
              {/* 既有的通知偏好表格 */}
              <table className="prefs-table">
                <thead>
                  <tr>
                    <th>事件</th>
                    <th>站內通知</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {preferences.map((pref) => (
                    <tr key={pref.event_type}>
                      <td className="event-label">
                        {EVENT_LABELS[pref.event_type] || pref.event_type}
                      </td>
                      <td>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={pref.in_app}
                            onChange={() => handleToggle(pref.event_type, 'in_app')}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </td>
                      <td>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={pref.email}
                            onChange={() => handleToggle(pref.event_type, 'email')}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Teams Webhook 管理區塊 */}
              <div className="webhook-section">
                <div className="webhook-section-header">
                  <h3>Teams Webhook 管理</h3>
                  <button className="webhook-guide-link" onClick={() => setShowGuide(true)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    設定教學
                  </button>
                </div>

                {webhooksLoading ? (
                  <div className="prefs-loading">載入中...</div>
                ) : (
                  <>
                    {/* Webhook 列表 */}
                    {webhooks.length === 0 && !isFormVisible ? (
                      <div className="webhook-empty">
                        尚未設定任何 Teams Webhook，新增後即可接收 Teams 通知。
                      </div>
                    ) : (
                      <div className="webhook-list">
                        {webhooks.map((webhook) => (
                          <div
                            key={webhook.id}
                            className={`webhook-item ${!webhook.is_active ? 'webhook-item--inactive' : ''}`}
                          >
                            <div className="webhook-item-info">
                              <div className="webhook-item-label">
                                {webhook.label || '未命名'}
                              </div>
                              <div className="webhook-item-url" title={webhook.webhook_url}>
                                {maskUrl(webhook.webhook_url)}
                              </div>
                              <div className="webhook-item-events">
                                訂閱: {getSubscriptionLabel(webhook.subscribed_events)}
                              </div>
                            </div>
                            <div className="webhook-item-actions">
                              <label className="toggle">
                                <input
                                  type="checkbox"
                                  checked={webhook.is_active}
                                  onChange={() => handleActiveToggle(webhook)}
                                />
                                <span className="toggle-slider" />
                              </label>
                              <button
                                className="webhook-btn webhook-btn--edit"
                                onClick={() => handleEditClick(webhook)}
                                title="編輯"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                  <path d="m15 5 4 4" />
                                </svg>
                              </button>
                              {deleteConfirmId === webhook.id ? (
                                <span className="delete-confirm">
                                  <button
                                    className="webhook-btn webhook-btn--danger"
                                    onClick={() => handleDelete(webhook.id)}
                                  >
                                    確認
                                  </button>
                                  <button
                                    className="webhook-btn"
                                    onClick={() => setDeleteConfirmId(null)}
                                  >
                                    取消
                                  </button>
                                </span>
                              ) : (
                                <button
                                  className="webhook-btn webhook-btn--danger"
                                  onClick={() => setDeleteConfirmId(webhook.id)}
                                  title="刪除"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18" />
                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 新增按鈕 */}
                    {!isFormVisible && (
                      <button className="webhook-add-btn" onClick={handleAddClick}>
                        + 新增 Webhook
                      </button>
                    )}

                    {/* 新增/編輯表單 */}
                    {isFormVisible && (
                      <div className="webhook-form">
                        <div className="webhook-form-title">
                          {editingId ? '編輯 Webhook' : '新增 Webhook'}
                        </div>

                        <div className="webhook-form-field">
                          <label>標籤</label>
                          <input
                            type="text"
                            placeholder="例如：我的工作通知頻道"
                            value={formData.label}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, label: e.target.value }))
                            }
                          />
                        </div>

                        <div className="webhook-form-field">
                          <label>Webhook URL</label>
                          <input
                            type="url"
                            placeholder="https://..."
                            value={formData.webhook_url}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, webhook_url: e.target.value }))
                            }
                          />
                        </div>

                        <div className="webhook-form-field">
                          <label>訂閱事件</label>
                          <div className="webhook-events-checkboxes">
                            {TEAMS_SUPPORTED_EVENTS.map((event) => (
                              <label key={event.value} className="webhook-event-checkbox">
                                <input
                                  type="checkbox"
                                  checked={formData.subscribed_events.includes(event.value)}
                                  onChange={() => handleEventToggle(event.value)}
                                />
                                <span>{event.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {testResult && (
                          <div
                            className={`webhook-test-result ${testResult.success ? 'webhook-test-result--success' : 'webhook-test-result--error'}`}
                          >
                            {testResult.success
                              ? '測試成功！請確認 Teams 頻道已收到訊息。'
                              : `測試失敗：${testResult.error}`}
                          </div>
                        )}

                        <div className="webhook-form-actions">
                          <button
                            className="webhook-btn webhook-btn--secondary"
                            onClick={handleTest}
                            disabled={!formData.webhook_url || testing}
                          >
                            {testing ? '測試中...' : '測試'}
                          </button>
                          <div className="webhook-form-actions-right">
                            <button className="webhook-btn" onClick={resetForm}>
                              取消
                            </button>
                            <button
                              className="webhook-btn webhook-btn--primary"
                              onClick={handleSave}
                              disabled={
                                !formData.webhook_url ||
                                formData.subscribed_events.length === 0 ||
                                saving
                              }
                            >
                              {saving ? '儲存中...' : '儲存'}
                            </button>
                          </div>
                        </div>

                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <SharedWebhookGuide isOpen={showGuide} onClose={() => setShowGuide(false)} />
      </div>
    </div>
  );
};

export default NotificationPreferences;
